import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import type { BingoStore } from '../../stores/bingo/BingoStore';
import type { MetagameBingoEvent } from '../../stores/bingo/types';
import type { SubscriptionStore } from '../../stores/subscription/SubscriptionStore';

import styles from './LobbyPassBanner.module.scss';

export interface BannerConfig {
  bannerClasses: string[];
  titleText: string;
  buttonText?: string;
  buttonClass?: string;
  buttonWrapperClass?: string;
  btnType?: 'main' | 'purple-blue';
  showButton: boolean;
  showCheckersBackground: boolean;
  dataTestId?: string;
  action: 'purchase' | 'enter-live' | 'acknowledge-last-result' | 'open-bingo-ftue';
  eventToDisplay: MetagameBingoEvent | null;
}

interface UseBannerConfigParams {
  bingoStore: BingoStore;
  subscriptionStore: SubscriptionStore;
}

export const useBannerConfig = ({ bingoStore }: UseBannerConfigParams): BannerConfig | null => {
  const { t } = useTranslation();

  const {
    currentEventStatus,
    closestBingoEvent,
    isParticipatingInClosestBingoEvent,
    hasBingoResultToShow,
    bingoResultToShow,
    hasEverParticipatedInBingo,
    nextUpcomingBingoEvent,
    hasNeverSeenBingoFtue,
  } = bingoStore;

  const { coinType } = bingoResultToShow || {};

  const isLiveBingo = currentEventStatus === 'live';
  const isCurrentEventFinalized = currentEventStatus === 'finalized';
  const shouldShowBingoResult = hasBingoResultToShow && bingoResultToShow && bingoStore.bingoResultHasWin && coinType;
  const bingoResultTypeClass = shouldShowBingoResult ? styles[`banner${coinType}`] : null;

  return useMemo(() => {
    const baseClasses = [styles.banner];

    if (shouldShowBingoResult && bingoResultTypeClass) {
      return {
        bannerClasses: [...baseClasses, styles.bannerGradient, bingoResultTypeClass],
        titleText: t('prime.scratchCard.bingo'),
        buttonText: t('prime.scratchCard.seePrize'),
        buttonWrapperClass: styles.enterButtonWrapper,
        buttonClass: styles.enterButton,
        btnType: 'purple-blue',
        showButton: true,
        showCheckersBackground: false,
        dataTestId: 'acknowledge-last-bingo-result-btn',
        action: 'acknowledge-last-result',
        eventToDisplay: null,
      };
    }

    const eventToDisplay = currentEventStatus === 'closed' ? nextUpcomingBingoEvent : closestBingoEvent;

    if (!eventToDisplay) return null;

    const bingoTypeClass = styles[`banner${eventToDisplay.coinType}`];
    const isFreeDaubEvent = !!eventToDisplay.game?.isFreeDaubEvent;
    const backgroundClass = isFreeDaubEvent ? styles.bannerFreeDaub : styles.bannerGradient;

    if (isLiveBingo && hasNeverSeenBingoFtue) {
      return {
        bannerClasses: [...baseClasses, styles.bannerCheckersPattern],
        titleText: t('prime.lobbyBanner.happeningNow'),
        buttonText: t('prime.lobbyBanner.joinNow'),
        buttonClass: styles.joinButton,
        btnType: 'main',
        showButton: true,
        showCheckersBackground: true,
        dataTestId: 'live_bingo_ftue_banner',
        action: 'open-bingo-ftue',
        eventToDisplay,
      };
    }

    if (!hasEverParticipatedInBingo) {
      let titleText: string = '';
      let buttonText: string = t('prime.lobbyBanner.joinNow');
      if (isLiveBingo) {
        titleText = t('prime.lobbyBanner.happeningNow');
        buttonText = t('prime.lobbyBanner.enterLive');
      } else if (currentEventStatus === 'aboutToStart') {
        titleText = t('prime.lobbyBanner.startingSoon');
        buttonText = t('prime.lobbyBanner.purchase');
      }

      return {
        bannerClasses: [...baseClasses, styles.bannerCheckersPattern],
        titleText,
        buttonText,
        buttonClass: styles.joinButton,
        btnType: 'main',
        showButton: true,
        showCheckersBackground: true,
        dataTestId: isLiveBingo ? 'enter-live-bingo-btn' : 'first-purchase-bingo-btn',
        action: isLiveBingo ? 'enter-live' : 'purchase',
        eventToDisplay,
      };
    }

    if (isParticipatingInClosestBingoEvent && !isCurrentEventFinalized) {
      let titleText: string = '';
      let buttonText: string | undefined;
      let action: BannerConfig['action'] = 'purchase';

      if (isLiveBingo) {
        titleText = t('prime.lobbyBanner.isLive');
        buttonText = t('prime.lobbyBanner.enterLive');
        action = 'enter-live';
      } else if (currentEventStatus === 'aboutToStart') {
        titleText = t('prime.lobbyBanner.startingSoon');
        buttonText = t('prime.lobbyBanner.purchase');
      } else {
        buttonText = t('prime.lobbyBanner.purchase');
      }

      return {
        bannerClasses: [...baseClasses, backgroundClass, bingoTypeClass],
        titleText,
        buttonText,
        buttonWrapperClass: styles.enterButtonWrapper,
        buttonClass: styles.enterButton,
        btnType: action === 'purchase' ? 'main' : 'purple-blue',
        showButton: true,
        showCheckersBackground: false,
        action,
        eventToDisplay,
      };
    }

    if (hasEverParticipatedInBingo) {
      let titleText = t('prime.lobbyBanner.startingSoon');
      let buttonText = t('prime.lobbyBanner.purchase');

      if (isLiveBingo) {
        titleText = t('prime.lobbyBanner.isLive');
        buttonText = t('prime.lobbyBanner.enterLive');
      } else if (currentEventStatus === 'aboutToStart') {
        titleText = t('prime.lobbyBanner.startingSoon');
      }

      return {
        bannerClasses: [...baseClasses, backgroundClass, bingoTypeClass],
        titleText,
        buttonText,
        buttonClass: styles.purchaseCards,
        btnType: isLiveBingo ? 'purple-blue' : 'main',
        showButton: true,
        showCheckersBackground: false,
        action: isLiveBingo ? 'enter-live' : 'purchase',
        eventToDisplay,
      };
    }

    return {
      bannerClasses: [...baseClasses, styles.bannerCheckersPattern],
      titleText: t('prime.lobbyBanner.purchase'),
      buttonText: t('prime.lobbyBanner.joinNow'),
      buttonClass: styles.joinButton,
      btnType: 'main',
      showButton: true,
      showCheckersBackground: true,
      action: 'purchase',
      eventToDisplay,
    };
  }, [
    shouldShowBingoResult,
    bingoResultTypeClass,
    closestBingoEvent,
    hasEverParticipatedInBingo,
    isParticipatingInClosestBingoEvent,
    isCurrentEventFinalized,
    t,
    isLiveBingo,
    currentEventStatus,
    nextUpcomingBingoEvent,
    hasNeverSeenBingoFtue,
  ]);
};
